import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Squares2X2Icon,
  DocumentMagnifyingGlassIcon,
  AcademicCapIcon,
  ChartBarIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ArrowRightStartOnRectangleIcon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  SwatchIcon,
  ChatBubbleLeftEllipsisIcon,
  QuestionMarkCircleIcon,
  VideoCameraIcon,
  DocumentPlusIcon,
} from '@heroicons/react/24/outline'
import { useApplications } from '../hooks/useApplications'
import { FeedbackModal } from './modals/FeedbackModal'
import { ProBadge } from './UpgradePrompt'
import { trackViewPricing } from '../utils/facebookPixel'
import { trackGAViewPricing } from '../utils/googleAnalytics'
import type { ThemeMode } from '../types'

interface MobileDrawerProps {
  isOpen: boolean
  onClose: () => void
  avatarUrl: string | null
  displayName: string | null
  email: string | null
  isPro: boolean
  effectivePlan: string
  onLogout: () => void
}

const mainNav = [
  { label: 'Dashboard', path: '/', icon: Squares2X2Icon },
  { label: 'Analytics', path: '/analytics', icon: ChartBarIcon },
  { label: 'Resume Analysis', path: '/resume', icon: DocumentMagnifyingGlassIcon },
  { label: 'Interview Quiz', path: '/quiz', icon: AcademicCapIcon },
  { label: 'Mock Interview', path: '/interview', icon: VideoCameraIcon, pro: true },
  { label: 'Resume Builder', path: '/resume-builder', icon: DocumentPlusIcon, pro: true },
  { label: 'AI Assistant', path: '/chat', icon: ChatBubbleLeftEllipsisIcon, pro: true },
]

const bottomNav = [
  { label: 'Settings', path: '/settings', icon: Cog6ToothIcon },
  { label: 'Help', path: '/help', icon: QuestionMarkCircleIcon },
]

const themeOptions: { value: ThemeMode; label: string; icon: typeof SunIcon }[] = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'system', label: 'System', icon: ComputerDesktopIcon },
]

export function MobileDrawer({
  isOpen,
  onClose,
  avatarUrl,
  displayName,
  email,
  isPro,
  effectivePlan,
  onLogout,
}: MobileDrawerProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { settings, handleSaveSettings } = useApplications()
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen && !isFeedbackOpen) return null

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const linkClass = (path: string) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive(path)
        ? 'bg-app-highlight text-app-accent'
        : 'text-app-muted hover:bg-app-subtle'
    }`

  const setTheme = (mode: ThemeMode) => {
    void handleSaveSettings({ ...settings, themeMode: mode })
  }

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <div className="fixed left-0 top-0 bottom-0 w-72 max-w-[80vw] flex flex-col border-r border-app-line bg-app-surface">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <Link to="/" onClick={onClose} className="inline-flex items-center transition hover:opacity-80">
                <img src="/logo-header-black.svg" alt="JobTrackr.it" className="h-10 w-auto dark:hidden" />
                <img src="/logo-header-white.svg" alt="JobTrackr.it" className="h-10 w-auto hidden dark:block" />
              </Link>
              <button
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-app-muted hover:bg-app-subtle transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Main navigation */}
            <nav className="px-3 space-y-1">
              {mainNav.map((item) => (
                'comingSoon' in item && item.comingSoon ? (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-app-muted cursor-default"
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                    <span className="ml-auto flex-shrink-0 rounded-full bg-app-subtle px-1.5 py-0.5 text-[9px] font-semibold text-app-muted whitespace-nowrap">
                      Soon
                    </span>
                  </div>
                ) : (
                  <Link key={item.path} to={item.path} onClick={onClose} className={linkClass(item.path)}>
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {item.label}
                    {'pro' in item && item.pro && !isPro && (
                      <span className="ml-auto">
                        <ProBadge onClick={() => { navigate(item.path); onClose() }} source="mobile_drawer" feature={item.label.toLowerCase().replace(/\s+/g, '_')} />
                      </span>
                    )}
                  </Link>
                )
              ))}
            </nav>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Bottom section */}
            <div className="px-3 pb-4 space-y-1">
              {/* Upgrade button for free users */}
              {!isPro && (
                <button
                  onClick={() => {
                    trackViewPricing('sidebar', 'upgrade_cta')
                    trackGAViewPricing('sidebar', 'upgrade_cta')
                    navigate('/pricing')
                    onClose()
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-semibold text-white transition-all mb-2 bg-none bg-app-primary hover:bg-app-primary-hover shadow-none"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
                  </svg>
                  Upgrade to Pro
                </button>
              )}

              {/* Bottom navigation (Settings, Billing) */}
              {bottomNav.map((item) => (
                <Link key={item.path} to={item.path} onClick={onClose} className={linkClass(item.path)}>
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {item.label}
                </Link>
              ))}

              {/* Feedback */}
              <button
                onClick={() => {
                  onClose()
                  setIsFeedbackOpen(true)
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-app-muted hover:bg-app-subtle transition-colors"
              >
                <ChatBubbleLeftEllipsisIcon className="h-5 w-5 flex-shrink-0" />
                Feedback
              </button>

              {/* Divider */}
              <div className="!my-2 h-px bg-app-subtle" />

              {/* Profile link */}
              <Link to="/profile" onClick={onClose} className={linkClass('/profile')}>
                <UserCircleIcon className="h-5 w-5 flex-shrink-0" />
                Profile
              </Link>

              {/* Billing link */}
              <Link to="/billing" onClick={onClose} className={linkClass('/billing')}>
                <CreditCardIcon className="h-5 w-5 flex-shrink-0" />
                Billing
              </Link>

              {/* Theme section */}
              <div className="px-3 pt-2 pb-1">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-app-muted mb-1">
                  <SwatchIcon className="h-3.5 w-3.5" />
                  Theme
                </div>
                {themeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors text-app-muted hover:bg-app-subtle"
                  >
                    {settings.themeMode === opt.value ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-app-primary flex-shrink-0" />
                    ) : (
                      <span className="h-1.5 w-1.5 flex-shrink-0" />
                    )}
                    <opt.icon className="h-4 w-4 flex-shrink-0" />
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="!my-2 h-px bg-app-subtle" />

              {/* User info */}
              <div className="flex items-center gap-3 rounded-lg px-3 py-2">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-app-line bg-app-page">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                  ) : (
                    <svg className="h-5 w-5 text-app-muted" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-medium text-app-ink">
                      {displayName || email || 'Signed in'}
                    </p>
                    {effectivePlan === 'pro' && (
                      <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide flex-shrink-0 bg-none bg-app-highlight text-app-accent hover:bg-app-highlight shadow-none">
                        <svg className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
                        </svg>
                        Pro
                      </span>
                    )}
                  </div>
                  {displayName && email && (
                    <p className="truncate text-xs text-app-muted">{email}</p>
                  )}
                </div>
              </div>

              {/* Sign out */}
              <button
                onClick={() => {
                  onClose()
                  onLogout()
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <ArrowRightStartOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal (rendered outside drawer so it stays visible after drawer closes) */}
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  )
}
